import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  FaBriefcase, FaSearch, FaMapMarkerAlt, FaClock, FaRupeeSign, FaEye, FaInfoCircle, FaHeart, FaRegHeart
} from "react-icons/fa";

const Jobs = () => {
  const { allJobs, searchQuery: globalSearchQuery, savedJobs = [], toggleSaveJob } = useOutletContext();
  const navigate = useNavigate();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState("");

  const roleDropdownRef = React.useRef(null);
  const deptDropdownRef = React.useRef(null);

  useEffect(() => {
    // Extract filters from only short jobs
    const shortJobs = allJobs.filter(job => job.jobType === "short");

    // Extract unique departments
    const depts = Array.from(new Set(shortJobs.map(job => job.department).filter(Boolean))).sort();
    setDepartments(depts);

    // Extract unique roles
    const roleNames = Array.from(new Set(shortJobs.map(job => job.role).filter(Boolean))).sort();
    setRoles(roleNames);

    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
        setIsDeptDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [allJobs]);

  const filteredJobs = allJobs.filter(job => {
    // Only show short jobs
    if (job.jobType !== "short") return false;

    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
      job.role.toLowerCase().includes(combinedQuery) ||
      job.location.toLowerCase().includes(combinedQuery) ||
      (job.department || "").toLowerCase().includes(combinedQuery);

    const matchesRole = roleFilter ? job.role === roleFilter : true;
    const matchesDept = departmentFilter ? job.department === departmentFilter : true;

    return matchesSearch && matchesRole && matchesDept;
  });

  const resetFilters = () => {
    setLocalSearchQuery("");
    setRoleFilter("");
    setDepartmentFilter("");
    setRoleSearchQuery("");
    setDeptSearchQuery("");
  };

  return (
    <div className="w-full h-full">

      {/* ── Header & Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">

          <div className="flex items-center gap-4 mr-auto">
            <div className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl text-orange-600 shadow-sm border border-orange-100/50">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-md">
                <FaBriefcase size={20} />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-500 to-orange-600 uppercase tracking-tight">SHORT JOBS</h1>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Quick Gigs & Short-term Roles</div>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-64">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search short jobs..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none uppercase tracking-widest"
            />
          </div>

          {/* Role Filter */}
          <div className="relative" ref={roleDropdownRef}>
            <button
              onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              className={`h-9 px-4 text-[10px] font-bold rounded-xl transition flex items-center gap-2 uppercase tracking-widest border ${roleFilter
                  ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
            >
              <FaBriefcase className={roleFilter ? 'text-white' : 'text-orange-500'} size={12} />
              Role {roleFilter && `: ${roleFilter}`}
            </button>

            {isRoleDropdownOpen && (
              <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl p-2 animate-in fade-in zoom-in-95 duration-200 origin-top">
                <div className="p-2 border-b border-gray-50 mb-1">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                    <input
                      type="text"
                      className="w-full py-2 pl-8 pr-3 text-[10px] bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-orange-500 outline-none transition-all font-bold uppercase tracking-wider"
                      placeholder="Search roles..."
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto no-scrollbar">
                  <div
                    onClick={() => { setRoleFilter(''); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                    className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${!roleFilter ? 'bg-orange-50 text-orange-600' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    All Roles
                  </div>
                  {roles
                    .filter(r => r.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                    .map((r, i) => (
                      <div
                        key={i}
                        onClick={() => { setRoleFilter(r); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                        className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${roleFilter === r ? 'bg-orange-50 text-orange-600' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        {r}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Reset */}
          {(localSearchQuery || roleFilter || departmentFilter) && (
            <button
              onClick={resetFilters}
              className="h-9 px-4 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition uppercase tracking-widest shadow-sm"
            >
              Clear Filters
            </button>
          )}

        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {filteredJobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-amber-500 to-orange-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Job Position</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Duration</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Location</th>
                  <th className="py-3 px-4 text-center font-bold">Experience</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Pay / Day</th>
                  <th className="py-3 px-4 text-center font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredJobs.map((job) => (
                  <tr key={job._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-center border-r border-gray-50">
                      <div className="text-xs font-bold text-gray-800 uppercase tracking-tight">{job.role}</div>
                      <div className="text-[9px] text-amber-600 font-black uppercase tracking-widest mt-0.5">{job.department || "General"}</div>
                    </td>
                    <td className="px-4 py-2 text-center hidden md:table-cell border-r border-gray-50">
                      <div className="flex flex-col items-center gap-1">
                        {(job.startTime || job.endTime) && (
                          <div className="text-[9px] font-black text-gray-500 uppercase tracking-tighter">
                            {job.startTime || "??:??"} - {job.endTime || "??:??"}
                          </div>
                        )}
                        <span className="text-[10px] font-bold text-orange-700 bg-orange-50 px-2.5 py-1 rounded-lg uppercase tracking-wider border border-orange-100 shadow-sm">
                          {job.totalDuration || "Short Term"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center hidden md:table-cell border-r border-gray-50">
                      <div className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        <FaMapMarkerAlt className="text-orange-400" size={10} />
                        {job.location}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center align-middle border-r border-gray-50">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100">
                        <FaClock className="text-amber-500" size={11} />
                        {job.experience || "Fresher"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center align-middle hidden md:table-cell border-r border-gray-50">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider bg-orange-50 px-2.5 py-1 rounded-md border border-orange-100">
                        <FaRupeeSign className="text-orange-600" size={11} />
                        {job.salary || "Negotiable"}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <button
                          onClick={() => toggleSaveJob(job._id)}
                          className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest rounded-lg border transition-all ${savedJobs.some(sj => (sj._id || sj) === job._id)
                              ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                            }`}
                        >
                          {savedJobs.some(sj => (sj._id || sj) === job._id) ? "Saved" : "Save"}
                        </button>
                        <button
                          onClick={() => navigate(`/jobs/${job._id}`)}
                          className="px-4 py-1.5 bg-orange-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-orange-700 transition-all flex items-center gap-1.5 shadow-md shadow-orange-100"
                        >
                          <FaEye size={10} /> View & Apply
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-16 text-center bg-gray-50/30">
            <div className="w-16 h-16 bg-white shadow-sm rounded-2xl flex items-center justify-center mx-auto mb-4 text-orange-200 border border-orange-50">
              <FaBriefcase size={24} />
            </div>
            <h3 className="text-sm font-black text-gray-800 mb-1 uppercase tracking-tight">No Short Jobs Available</h3>
            <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase">Check back later for quick gig opportunities.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-8 pb-4">
        <FaInfoCircle className="text-orange-400" />
        Timely Health Recruitment Tracker • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default Jobs;